import React, { useEffect, useMemo } from "react";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import { InputAdornment, Typography } from "@mui/material";
import PropTypes from "prop-types";

import {
  getOptionValue,
  gstOptions,
  otherUtilityApplicationOptions,
} from "common/enums";

import InputFieldText from "Components/Common/InputFieldText";
import DateField from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";

import { onlyNumberKeys } from "utils";

import { useGetLazyConsumerNumbersQuery } from "apis/User";

const RemovalOfConnectionService = ({ formik, t }) => {
  const {
    values: {
      id,
      consumerNumber,
      applicationTypeId,
      utilityConsumerId,
      utilities = [],
      applicant: { applicantTypeId, isGSTRegistered },
      extraValues: { isPanNumber, isGstNumber, applicantType },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    },
    setFieldValue,
  } = formik;

  const [
    getConsumerNumbers,
    {
      data: { data: consumersOptions } = {},
      isLoading: isConsumerOptionsLoading,
    },
  ] = useGetLazyConsumerNumbersQuery();

  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      )?.id === applicantTypeId,
    [applicantType, applicantTypeId]
  );

  const isDisable = useMemo(
    () =>
      (inProgressApplicationId !== id && !isAllApplicationsProcessCompleted) ||
      payableBillAmount > 0,
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    ]
  );

  useEffect(() => {
    (async () => {
      if (utilities.length) {
        try {
          await getConsumerNumbers({
            applicationType: applicationTypeId,
            utilityId: utilities[0].value,
          });
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, [utilities]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <Typography className="mb-3" lineHeight="1" variant="h6">
            {t("Applicant Details")}
          </Typography>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <SelectField
            label={t("Utility") + "*"}
            name="utilities"
            formik={formik}
            value={utilities[0] ?? ""}
            onChange={(_, option) => {
              setFieldValue("utilities", [option]);
              setFieldValue("utilityConsumerId", "");
            }}
            options={otherUtilityApplicationOptions}
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Customer Number") + "*"}
            name="utilityConsumerId"
            formik={formik}
            loading={isConsumerOptionsLoading}
            options={consumersOptions || []}
            getOptionLabel={(option) => option.consumerNumber}
            getOptionValue={(option) => option.id}
            disabled={!utilities.length}
          />
        </Col>

        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Customer Details")}
        </Typography>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Customer Name")}
            name="consumerName"
            formik={formik}
            disabled
          />
        </Col>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Tariff Category")}
            name="tariffName"
            formik={formik}
            disabled
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Usage Purpose")}
            name="extraValues.premiseUsagePurpose"
            formik={formik}
            disabled
          />
        </Col>
        {!isPanNumber && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
              name="applicant.panNumber"
              formik={formik}
              onChange={(e) =>
                setFieldValue(
                  "applicant.panNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
              disabled={isDisable}
            />
          </Col>
        )}
        {!isGstNumber && (
          <>
            <Col md={4} sm={6}>
              <SelectField
                label={t("GST") + "*"}
                name="applicant.isGSTRegistered"
                onChange={(_, { value }) => {
                  setFieldValue("applicant.isGSTRegistered", value);
                  if (!value) {
                    setFieldValue("applicant.gstNumber", "");
                  }
                }}
                formik={formik}
                options={gstOptions}
                disabled={isDisable}
              />
            </Col>

            {isGSTRegistered && (
              <Col md={4} sm={6}>
                <InputFieldText
                  label={t("GST Number") + "*"}
                  name="applicant.gstNumber"
                  formik={formik}
                  onChange={(e) =>
                    setFieldValue(
                      "applicant.gstNumber",
                      e.target.value.toUpperCase()
                    )
                  }
                  placeholder="GST Number"
                  disabled={isDisable}
                />
              </Col>
            )}
          </>
        )}
        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Load Details")}
        </Typography>

        <Row>
          {utilities.some(
            ({ value }) => value === getOptionValue("utilityOptions", "DCS")
          ) && (
            <>
              <Col md={4}>
                <InputFieldText
                  label={t("Existing Contract Demand")}
                  name="dcsDemand.contractDemand"
                  formik={formik}
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">TR</InputAdornment>
                    ),
                    inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                  }}
                  disabled
                />
              </Col>
            </>
          )}
          {utilities.some(
            ({ value }) =>
              value === getOptionValue("utilityOptions", "Water") ||
              value === getOptionValue("utilityOptions", "Sewage")
          ) && (
            <>
              <Col md={4}>
                <InputFieldText
                  label={t("Existing Water Demand")}
                  name="waterSewageDemand.waterDemand"
                  formik={formik}
                  type="number"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Liters/day</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Col>
              <Col md={4}>
                <InputFieldText
                  label={t("Existing Flushing Demand")}
                  name="waterSewageDemand.flushing"
                  formik={formik}
                  type="number"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Liters/day</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Col>
              <Col md={4}>
                <InputFieldText
                  label={t("Existing Domestic Demand")}
                  name="waterSewageDemand.domestic"
                  formik={formik}
                  type="number"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Liters/day</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Col>
              <Col md={4}>
                <InputFieldText
                  label={t("Existing Gardening Demand")}
                  name="waterSewageDemand.gardening"
                  formik={formik}
                  type="number"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Liters/day</InputAdornment>
                    ),
                    inputProps: {
                      min: 0,
                      onKeyDown: (e) => onlyNumberKeys(e, true),
                    },
                  }}
                />
              </Col>
            </>
          )}
          <Col md={4}>
            <DateField
              label={t("Connection End Date") + "*"}
              name="disconnectionDate"
              formik={formik}
              format="DD-MM-YYYY"
              isDisable={false}
              disabled={isDisable}
              placeholder="Select Connection End Date"
              disablePast
            />
          </Col>
        </Row>
      </Row>

      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!utilityConsumerId &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          {t(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          )}
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
};

RemovalOfConnectionService.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(RemovalOfConnectionService);
